import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayout } from '@client/components/_core/layout/PageLayout';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Link } from '@client/components/Common/Link';
import { AddRegisterModal } from '@client/components/registers/AddRegisterModal';
import { RegisterBuilderDrawer } from '@client/components/registers/RegisterBuilderDrawer';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { REGISTER_TEMPLATES } from '@shared/constants/business/register-templates';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card, Empty, Skeleton } from 'antd';
import { Gauge, KeyRound, Plus, Server, Table2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { z } from 'zod';

// CUR-644 phase 2 — the registers index (cards, template gallery, builder),
// transcribed from the prototype contract (registers @ 65bf8a7,
// RegistersIndex.tsx).

/**
 * `?open=gallery` opens the Add a register gallery on arrival — the landing
 * for the What's new Registers note's call to action (CUR-1094 req. 3) and
 * for any other "start here" link. Optional so the plain URL stays clean; a
 * malformed value is ignored rather than erroring the route.
 */
const REGISTERS_SEARCH_SCHEMA = z.object({
    open: z.literal('gallery').optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/compliance/registers/')({
    validateSearch: zodValidator(REGISTERS_SEARCH_SCHEMA),
    staticData: {
        ui: {
            title: 'Registers',
            // One line, no full stop, no marketing voice. The previous copy ran
            // to two lines and squeezed the header's New register button until
            // its label wrapped.
            subtitle: 'Records your business keeps, linked to the risks, controls and obligations they belong to',
        },
    },
    component: RegistersIndexPage,
});

/** Icon vocabulary from the prototype (server/key/table/gauge). */
const ICONS: Record<string, React.ReactNode> = {
    server: <Server size={16} />,
    key: <KeyRound size={16} />,
    table: <Table2 size={16} />,
    gauge: <Gauge size={16} />,
};

function RegistersIndexPage() {
    const { orgId } = Route.useParams();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const canUpdate = can('register:update');
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [builderOpen, setBuilderOpen] = useState(false);
    const { data, isPending, error, refetch } = trpc.register.listRegisters.useQuery();

    // Deep-link entry: open the gallery once the list has resolved (see the
    // header comment below on why the gallery must wait for `data`), then drop
    // the one-shot instruction so a reload restores the plain index.
    const { open } = Route.useSearch();
    useEffect(() => {
        if (open !== 'gallery' || !canUpdate || !data) return;
        setGalleryOpen(true);
        void navigate({
            to: '/console/$orgId/compliance/registers',
            params: { orgId },
            search: {},
            replace: true,
        });
    }, [open, canUpdate, data, navigate, orgId]);

    const installedTemplateKeys = (data?.rows ?? [])
        .map((register) => register.templateKey)
        .filter((key): key is string => key !== null);
    const availableTemplates = REGISTER_TEMPLATES.filter(
        (template) => !installedTemplateKeys.includes(template.key),
    ).length;

    function openRegister(id: bigint) {
        void navigate({
            to: '/console/$orgId/compliance/registers/$registerId',
            params: { orgId, registerId: id },
        });
    }

    return (
        <PageController route={Route}>
            {/* Page-level creation belongs in the header's primary action slot —
                a hand-rolled right-aligned row below the header spends a whole
                row on one button and detaches it from the page title.

                Gated on `data` as well as the permission: `installedTemplateKeys`
                is empty until the list resolves, so opening the gallery in that
                window offers every template as uninstalled — including ones the
                org already has, which the server then rejects. The button used to
                sit inside the loaded branch and inherited this for free. */}
            {canUpdate && data && (
                <PageLayout.Header
                    primaryActionAddon={
                        // The header row is a flex line shared with the subtitle;
                        // without these the long subtitle compresses the button
                        // until its label wraps to two lines.
                        <Button
                            type="primary"
                            icon={<Plus />}
                            className="shrink-0 whitespace-nowrap"
                            onClick={() => setGalleryOpen(true)}
                        >
                            New register
                        </Button>
                    }
                />
            )}
            {error ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load registers" />
            ) : isPending || !data ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <div className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {data.rows.map((register) => (
                            <Card
                                key={register.id.toString()}
                                size="small"
                                hoverable
                                onClick={() => openRegister(register.id)}
                                className="cursor-pointer"
                            >
                                <div className="mb-2 flex items-center gap-2">
                                    <span className="flex h-7 w-7 items-center justify-center rounded bg-accent-50 text-accent-600">
                                        {(register.icon && ICONS[register.icon]) ?? ICONS.table}
                                    </span>
                                    {/* A real link inside the clickable card: keyboard and
                                        screen-reader users get a focusable route to the
                                        register; the card onClick stays for pointer users.
                                        stopPropagation keeps a title click from also firing
                                        the card handler (double navigation). */}
                                    <Link
                                        to="/console/$orgId/compliance/registers/$registerId"
                                        params={{ orgId, registerId: register.id }}
                                        onClick={(event) => event.stopPropagation()}
                                        className="font-medium text-foreground text-sm"
                                    >
                                        {register.name}
                                    </Link>
                                </div>
                                <p className="mb-3 min-h-10 text-muted-foreground text-xs">{register.description}</p>
                                {/* Neither of these is a classification: one is a count,
                                    the other a free-text regulation reference. As Tags
                                    they read as a fixed vocabulary and looked like the
                                    same kind of thing as each other. Muted text, one
                                    line, matching the register page. */}
                                <div className="flex flex-wrap items-center gap-1.5 text-subtle-foreground text-xs">
                                    <span>
                                        {register._count.Records} {register._count.Records === 1 ? 'record' : 'records'}
                                    </span>
                                    {register.regulation && <span>· {register.regulation}</span>}
                                </div>
                            </Card>
                        ))}

                        {canUpdate && (
                            <Card size="small" hoverable className="border-dashed">
                                <button
                                    type="button"
                                    onClick={() => setGalleryOpen(true)}
                                    className="flex h-full min-h-33 w-full cursor-pointer flex-col items-center justify-center gap-2 text-muted-foreground"
                                >
                                    <Plus size={20} />
                                    <span className="text-sm">Add a register</span>
                                    <span className="text-subtle-foreground text-xs">
                                        {availableTemplates} {availableTemplates === 1 ? 'template' : 'templates'}{' '}
                                        available
                                    </span>
                                </button>
                            </Card>
                        )}
                    </div>

                    {/* Unreachable with today's static role maps — every role granting
                        register:read also grants register:update, so the dashed add
                        tile above always covers the empty case. Kept deliberately:
                        it is the only thing standing between a read-only viewer and
                        a blank page, and REG-P2 (per-register visibility) is a
                        planned follow-up that makes read-without-update real. */}
                    {data.rows.length === 0 && !canUpdate && (
                        <div className="py-10">
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No registers yet" />
                        </div>
                    )}
                </div>
            )}

            <AddRegisterModal
                open={galleryOpen}
                installedTemplateKeys={installedTemplateKeys}
                onClose={() => setGalleryOpen(false)}
                onInstalled={openRegister}
                onStartFromScratch={() => setBuilderOpen(true)}
            />
            <RegisterBuilderDrawer open={builderOpen} onClose={() => setBuilderOpen(false)} onCreated={openRegister} />
        </PageController>
    );
}
